import { useEffect, useState, useMemo } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { adminUsers, type UserAdmin } from '../../api/admin';
import {
  Users, Search, Mail, Phone, Calendar, ShoppingCart,
  ChevronDown, ChevronUp, UserCheck, UserX, IndianRupee, MapPin, Pencil, X, Save, Trash2,
} from 'lucide-react';

type OrderSummary = {
  id: string;
  status: string;
  total: number;
  items_count: number;
  created_at: string;
};

type UserAddress = {
  id: string;
  address_line: string;
  city: string;
  state: string;
  pincode: string;
  is_default: boolean;
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'text-yellow-500 bg-yellow-500/10',
  confirmed: 'text-blue-500 bg-blue-500/10',
  shipped: 'text-purple-500 bg-purple-500/10',
  delivered: 'text-emerald-500 bg-emerald-500/10',
  cancelled: 'text-red-500 bg-red-500/10',
};

export default function UsersPage() {
  const { isDark } = useTheme();
  const [users, setUsers] = useState<UserAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [userOrders, setUserOrders] = useState<Record<string, OrderSummary[]>>({});
  const [userAddresses, setUserAddresses] = useState<Record<string, UserAddress[]>>({});
  const [editUser, setEditUser] = useState<UserAdmin | null>(null);
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', email: '', phone: '', is_active: true });
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [editAddr, setEditAddr] = useState<{ userId: string; addr: UserAddress } | null>(null);
  const [addrForm, setAddrForm] = useState({ address_line: '', city: '', state: '', pincode: '', is_default: false });
  const [addrSaving, setAddrSaving] = useState(false);
  const [addrError, setAddrError] = useState('');

  useEffect(() => {
    adminUsers.list()
      .then(data => setUsers(data))
      .catch(err => { console.error(err); setError(err.message || 'Failed to load users'); })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!search) return users;
    const q = search.toLowerCase();
    return users.filter(u =>
      u.first_name?.toLowerCase().includes(q) ||
      u.last_name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.phone?.toLowerCase().includes(q)
    );
  }, [users, search]);

  const expandUser = async (userId: string) => {
    if (expandedId === userId) { setExpandedId(null); return; }
    setExpandedId(userId);
    if (!userOrders[userId]) {
      try {
        const detail = await adminUsers.get(userId);
        setUserOrders(prev => ({ ...prev, [userId]: detail.orders }));
        setUserAddresses(prev => ({ ...prev, [userId]: detail.addresses ?? [] }));
      } catch (err) { console.error(err); }
    }
  };

  const fmt = (n: number) => `\u20B9${n?.toLocaleString('en-IN') ?? 0}`;

  const openEdit = (user: UserAdmin, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditUser(user);
    setEditForm({
      first_name: user.first_name ?? '',
      last_name: user.last_name ?? '',
      email: user.email ?? '',
      phone: user.phone ?? '',
      is_active: user.is_active,
    });
    setEditError('');
  };

  const saveEdit = async () => {
    if (!editUser) return;
    setSaving(true);
    setEditError('');
    try {
      const updated = await adminUsers.update(editUser.id, {
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        email: editForm.email,
        phone: editForm.phone || null,
        is_active: editForm.is_active,
      });
      setUsers(prev => prev.map(u => u.id === editUser.id ? { ...u, ...updated } : u));
      setEditUser(null);
    } catch (err: unknown) {
      setEditError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const deleteUser = async (userId: string) => {
    setDeleting(true);
    try {
      await adminUsers.delete(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
      if (expandedId === userId) setExpandedId(null);
      setDeleteConfirmId(null);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const openEditAddr = (userId: string, addr: UserAddress) => {
    setEditAddr({ userId, addr });
    setAddrForm({ address_line: addr.address_line, city: addr.city, state: addr.state, pincode: addr.pincode, is_default: addr.is_default });
    setAddrError('');
  };

  const saveAddr = async () => {
    if (!editAddr) return;
    setAddrSaving(true);
    setAddrError('');
    try {
      const updated = await adminUsers.updateAddress(editAddr.userId, editAddr.addr.id, addrForm);
      setUserAddresses(prev => ({
        ...prev,
        [editAddr.userId]: prev[editAddr.userId].map(a =>
          a.id === editAddr.addr.id ? { ...a, ...updated } : (updated.is_default ? { ...a, is_default: false } : a)
        ),
      }));
      setEditAddr(null);
    } catch (err: unknown) {
      setAddrError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setAddrSaving(false);
    }
  };

  const deleteAddr = async (userId: string, addressId: string) => {
    if (!confirm('Delete this address?')) return;
    try {
      await adminUsers.deleteAddress(userId, addressId);
      setUserAddresses(prev => ({
        ...prev,
        [userId]: prev[userId].filter(a => a.id !== addressId),
      }));
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to delete address');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
    </div>
  );

  if (error) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3">
      <UserX size={40} className="text-red-500" />
      <p className="text-red-500 text-sm">{error}</p>
      <button onClick={() => window.location.reload()} className="text-sm text-emerald-500 hover:underline">Retry</button>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Users size={24} className="text-emerald-500" /> Users
        </h1>
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {users.length} registered customers
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className={`rounded-xl p-4 border ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center"><Users size={20} className="text-white" /></div>
            <div>
              <p className={`text-xs uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Total Users</p>
              <p className="text-xl font-bold">{users.length}</p>
            </div>
          </div>
        </div>
        <div className={`rounded-xl p-4 border ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center"><ShoppingCart size={20} className="text-white" /></div>
            <div>
              <p className={`text-xs uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Total Orders</p>
              <p className="text-xl font-bold">{users.reduce((acc, u) => acc + u.orders_count, 0)}</p>
            </div>
          </div>
        </div>
        <div className={`rounded-xl p-4 border ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500 flex items-center justify-center"><IndianRupee size={20} className="text-white" /></div>
            <div>
              <p className={`text-xs uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Total Revenue</p>
              <p className="text-xl font-bold">{fmt(users.reduce((acc, u) => acc + u.total_spent, 0))}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
        <input type="text" placeholder="Search by name, email, phone..." value={search} onChange={e => setSearch(e.target.value)}
          className={`w-full pl-9 pr-4 py-2.5 rounded-lg border text-sm outline-none transition-colors ${isDark ? 'bg-gray-900 border-gray-800 text-white placeholder-gray-500 focus:border-gray-600' : 'bg-white border-gray-200 placeholder-gray-400 focus:border-gray-300'}`} />
      </div>

      {/* Users table */}
      <div className={`rounded-xl border overflow-x-auto ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className="min-w-[700px]">
        {/* Header row */}
        <div className={`grid grid-cols-[2fr_1.5fr_1fr_1fr_1fr_auto] gap-4 px-4 py-3 text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-gray-900/80 text-gray-500 border-b border-gray-800' : 'bg-gray-50 text-gray-400 border-b border-gray-200'}`}>
          <div>Name</div><div>Email</div><div>Phone</div><div className="text-center">Orders</div><div className="text-right">Spent</div><div></div>
        </div>

        {/* Empty state */}
        {filtered.length === 0 && (
          <div className={`text-center py-16 ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
            <Users size={48} className={`mx-auto mb-3 ${isDark ? 'text-gray-700' : 'text-gray-200'}`} />
            <p className={`text-sm font-medium ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No users found</p>
          </div>
        )}

        {/* User rows */}
        {filtered.map(user => {
          const isExpanded = expandedId === user.id;
          const orders = userOrders[user.id] ?? [];
          const addresses = userAddresses[user.id] ?? [];
          return (
            <div key={user.id}>
              {/* Row */}
              <div
                className={`grid grid-cols-[2fr_1.5fr_1fr_1fr_1fr_auto] gap-4 px-4 py-3.5 items-center cursor-pointer transition-colors ${isDark ? 'bg-gray-900 hover:bg-gray-800/40 border-b border-gray-800/50' : 'bg-white hover:bg-gray-50 border-b border-gray-100'}`}
                onClick={() => expandUser(user.id)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                    {user.first_name?.[0] ?? ''}{user.last_name?.[0] ?? ''}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{user.first_name} {user.last_name}</p>
                    <p className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                      Joined {new Date(user.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                </div>
                <p className={`text-sm truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{user.email}</p>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{user.phone || '\u2014'}</p>
                <p className="text-sm font-semibold text-center">{user.orders_count}</p>
                <p className="text-sm font-bold text-right">{fmt(user.total_spent)}</p>
                <div>{isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</div>
              </div>

              {/* Expanded detail */}
              {isExpanded && (
                <div className={`px-6 py-4 ${isDark ? 'bg-gray-950/50 border-b border-gray-800' : 'bg-gray-50/50 border-b border-gray-100'}`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* User details */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <p className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>User Details</p>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => openEdit(user, e)}
                            className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg transition-colors bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20"
                          >
                            <Pencil size={12} /> Edit
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(user.id); }}
                            className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg transition-colors bg-red-500/10 text-red-500 hover:bg-red-500/20"
                          >
                            <Trash2 size={12} /> Delete
                          </button>
                        </div>
                      </div>
                      <div className={`space-y-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        <p className="flex items-center gap-2 font-medium">
                          <UserCheck size={14} className="text-emerald-500" />
                          {user.first_name} {user.last_name}
                          {user.is_active
                            ? <span className="text-xs bg-emerald-500/10 text-emerald-500 px-2 py-0.5 rounded-full">Active</span>
                            : <span className="text-xs bg-red-500/10 text-red-500 px-2 py-0.5 rounded-full">Inactive</span>}
                        </p>
                        <p className="flex items-center gap-2"><Mail size={13} /> {user.email}</p>
                        <p className="flex items-center gap-2"><Phone size={13} /> {user.phone || 'Not provided'}</p>
                        <p className="flex items-center gap-2">
                          <Calendar size={13} /> Joined {new Date(user.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                        <p className="flex items-center gap-2">
                          <IndianRupee size={13} /> Total spent: <span className="font-bold text-emerald-500">{fmt(user.total_spent)}</span>
                        </p>
                      </div>

                      {/* Addresses */}
                      <div className="mt-4">
                        <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          Addresses ({addresses.length})
                        </p>
                        {addresses.length === 0 ? (
                          <p className={`text-sm ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>No addresses saved</p>
                        ) : (
                          <div className="space-y-2">
                            {addresses.map(a => (
                              <div key={a.id} className={`flex items-start gap-2 text-sm p-2 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-white'}`}>
                                <MapPin size={13} className="mt-0.5 shrink-0 text-blue-500" />
                                <div className="flex-1 min-w-0">
                                  <p>{a.address_line}</p>
                                  <p className={isDark ? 'text-gray-500' : 'text-gray-400'}>
                                    {a.city}, {a.state} — {a.pincode}
                                  </p>
                                  {a.is_default && <span className="text-xs bg-blue-500/10 text-blue-500 px-2 py-0.5 rounded-full">Default</span>}
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                  <button
                                    onClick={() => openEditAddr(user.id, a)}
                                    className={`p-1 rounded transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-500 hover:text-blue-400' : 'hover:bg-gray-100 text-gray-400 hover:text-blue-500'}`}
                                    title="Edit address"
                                  >
                                    <Pencil size={12} />
                                  </button>
                                  <button
                                    onClick={() => deleteAddr(user.id, a.id)}
                                    className={`p-1 rounded transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-500 hover:text-red-400' : 'hover:bg-gray-100 text-gray-400 hover:text-red-500'}`}
                                    title="Delete address"
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Order history */}
                    <div>
                      <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        Order History ({user.orders_count})
                      </p>
                      {orders.length === 0 ? (
                        <p className={`text-sm ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                          {user.orders_count === 0 ? 'No orders yet' : 'Loading...'}
                        </p>
                      ) : (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {orders.map(o => (
                            <div key={o.id} className={`flex items-center justify-between p-2.5 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-white'}`}>
                              <div>
                                <p className={`text-xs font-mono ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>#{o.id.slice(0, 8)}</p>
                                <p className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                                  {new Date(o.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                  {' \u00B7 '}{o.items_count} item(s)
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-bold">{fmt(o.total)}</p>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[o.status] ?? 'text-gray-500 bg-gray-500/10'}`}>
                                  {o.status}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      </div>

      {/* Edit User Modal */}
      {editUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setEditUser(null)}>
          <div
            className={`w-full max-w-md rounded-2xl p-6 shadow-xl ${isDark ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-gray-200'}`}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Pencil size={18} className="text-emerald-500" /> Edit User
              </h2>
              <button onClick={() => setEditUser(null)} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}>
                <X size={18} />
              </button>
            </div>

            {editError && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/10 text-red-500 text-sm">{editError}</div>
            )}

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`text-xs font-medium mb-1 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>First Name</label>
                  <input
                    type="text"
                    value={editForm.first_name}
                    onChange={e => setEditForm(prev => ({ ...prev, first_name: e.target.value }))}
                    className={`w-full px-3 py-2 rounded-lg border text-sm outline-none transition-colors ${isDark ? 'bg-gray-800 border-gray-700 text-white focus:border-emerald-500' : 'bg-gray-50 border-gray-200 focus:border-emerald-500'}`}
                  />
                </div>
                <div>
                  <label className={`text-xs font-medium mb-1 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Last Name</label>
                  <input
                    type="text"
                    value={editForm.last_name}
                    onChange={e => setEditForm(prev => ({ ...prev, last_name: e.target.value }))}
                    className={`w-full px-3 py-2 rounded-lg border text-sm outline-none transition-colors ${isDark ? 'bg-gray-800 border-gray-700 text-white focus:border-emerald-500' : 'bg-gray-50 border-gray-200 focus:border-emerald-500'}`}
                  />
                </div>
              </div>

              <div>
                <label className={`text-xs font-medium mb-1 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={e => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                  className={`w-full px-3 py-2 rounded-lg border text-sm outline-none transition-colors ${isDark ? 'bg-gray-800 border-gray-700 text-white focus:border-emerald-500' : 'bg-gray-50 border-gray-200 focus:border-emerald-500'}`}
                />
              </div>

              <div>
                <label className={`text-xs font-medium mb-1 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Phone</label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={e => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="e.g. 9876543210"
                  className={`w-full px-3 py-2 rounded-lg border text-sm outline-none transition-colors ${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-600 focus:border-emerald-500' : 'bg-gray-50 border-gray-200 placeholder-gray-400 focus:border-emerald-500'}`}
                />
              </div>

              <div className="flex items-center gap-3">
                <label className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Account Status</label>
                <button
                  type="button"
                  onClick={() => setEditForm(prev => ({ ...prev, is_active: !prev.is_active }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${editForm.is_active ? 'bg-emerald-500' : isDark ? 'bg-gray-700' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editForm.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
                <span className={`text-xs ${editForm.is_active ? 'text-emerald-500' : 'text-red-500'}`}>
                  {editForm.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setEditUser(null)}
                className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'}`}
              >
                Cancel
              </button>
              <button
                onClick={saveEdit}
                disabled={saving || !editForm.first_name.trim() || !editForm.last_name.trim() || !editForm.email.trim()}
                className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg font-medium bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? (
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <Save size={14} />
                )}
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setDeleteConfirmId(null)}>
          <div
            className={`w-full max-w-sm rounded-2xl p-6 shadow-xl ${isDark ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-gray-200'}`}
            onClick={e => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                <Trash2 size={24} className="text-red-500" />
              </div>
              <h3 className="text-lg font-bold mb-2">Delete User?</h3>
              <p className={`text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                This will permanently delete the user, their orders, addresses, cart, and reviews. This action cannot be undone.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setDeleteConfirmId(null)}
                  className={`px-5 py-2 text-sm rounded-lg font-medium transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteUser(deleteConfirmId)}
                  disabled={deleting}
                  className="flex items-center gap-2 px-5 py-2 text-sm rounded-lg font-medium bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 transition-colors"
                >
                  {deleting ? (
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <Trash2 size={14} />
                  )}
                  {deleting ? 'Deleting...' : 'Delete User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Address Modal */}
      {editAddr && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setEditAddr(null)}>
          <div
            className={`w-full max-w-md rounded-2xl p-6 shadow-xl ${isDark ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-gray-200'}`}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <MapPin size={18} className="text-blue-500" /> Edit Address
              </h2>
              <button onClick={() => setEditAddr(null)} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}>
                <X size={18} />
              </button>
            </div>

            {addrError && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/10 text-red-500 text-sm">{addrError}</div>
            )}

            <div className="space-y-4">
              <div>
                <label className={`text-xs font-medium mb-1 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Address Line</label>
                <input
                  type="text"
                  value={addrForm.address_line}
                  onChange={e => setAddrForm(prev => ({ ...prev, address_line: e.target.value }))}
                  className={`w-full px-3 py-2 rounded-lg border text-sm outline-none transition-colors ${isDark ? 'bg-gray-800 border-gray-700 text-white focus:border-blue-500' : 'bg-gray-50 border-gray-200 focus:border-blue-500'}`}
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={`text-xs font-medium mb-1 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>City</label>
                  <input
                    type="text"
                    value={addrForm.city}
                    onChange={e => setAddrForm(prev => ({ ...prev, city: e.target.value }))}
                    className={`w-full px-3 py-2 rounded-lg border text-sm outline-none transition-colors ${isDark ? 'bg-gray-800 border-gray-700 text-white focus:border-blue-500' : 'bg-gray-50 border-gray-200 focus:border-blue-500'}`}
                  />
                </div>
                <div>
                  <label className={`text-xs font-medium mb-1 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>State</label>
                  <input
                    type="text"
                    value={addrForm.state}
                    onChange={e => setAddrForm(prev => ({ ...prev, state: e.target.value }))}
                    className={`w-full px-3 py-2 rounded-lg border text-sm outline-none transition-colors ${isDark ? 'bg-gray-800 border-gray-700 text-white focus:border-blue-500' : 'bg-gray-50 border-gray-200 focus:border-blue-500'}`}
                  />
                </div>
                <div>
                  <label className={`text-xs font-medium mb-1 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>PIN Code</label>
                  <input
                    type="text"
                    value={addrForm.pincode}
                    onChange={e => setAddrForm(prev => ({ ...prev, pincode: e.target.value }))}
                    className={`w-full px-3 py-2 rounded-lg border text-sm outline-none transition-colors ${isDark ? 'bg-gray-800 border-gray-700 text-white focus:border-blue-500' : 'bg-gray-50 border-gray-200 focus:border-blue-500'}`}
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={addrForm.is_default}
                  onChange={e => setAddrForm(prev => ({ ...prev, is_default: e.target.checked }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Set as default address</span>
              </label>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setEditAddr(null)}
                className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'}`}
              >
                Cancel
              </button>
              <button
                onClick={saveAddr}
                disabled={addrSaving || !addrForm.address_line.trim() || !addrForm.city.trim() || !addrForm.state.trim() || !addrForm.pincode.trim()}
                className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg font-medium bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {addrSaving ? (
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <Save size={14} />
                )}
                {addrSaving ? 'Saving...' : 'Save Address'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
